/*
 * preen-header-autohide.js - units collection-mobile-header (Jonah 2026-10-06; site-wide 2026-10-07) and
 * header-compact-logo (Jonah 2026-10-07: a smaller logo while scrolled, and a smoother slide; revision 2: the compact
 * state stays on while the header is slid away mid-page, phone thresholds at the lift, the logo layer promoted) and
 * header-usp-strip (Jonah 2026-10-08: a static info strip above the header on the home, collection and product
 * pages, a shorter beige forehead, and no white band under the beige box, so the box floats over the page).
 * Every page that renders the Preen header, phone + tablet only ((max-width: 1024.4999px), the xs + sm bands of
 * snippets/preen-pf-grid): the header (logo + beige box, i.e. the header-group section) sticks to the top of the
 * screen, slides away while the visitor scrolls down and slides back on a scroll up. Away from the top of the page it
 * is COMPACT: the Preen Pets logo at half size (phones: centred, with the space above and below it halved; tablets:
 * in place, left of the beige box), the beige box unchanged. Compact depends on the scroll position only, so it stays
 * on while the header is slid away mid-page: every mid-page hide or show moves one layer (the header) and the logo
 * never zooms during a slide; the logo changes size only near the top of the page. At the top of the page it is
 * exactly the header as before. Laptop/desktop: nothing changes.
 * Loaded once by sections/preen-header.liquid (defer); the CSS it needs is the small <style> next to that tag.
 * Vanilla JS, no dependencies, no storage, no requests. window.__preenHeaderAutohide guards a second load.
 *
 * How: position: sticky on the header-group wrapper (a direct child of body), so the header keeps its full place
 * in the page at all times. On the home, collection and product pages the wrapper starts with the info strip
 * (snippets/preen-info-strip.liquid): its sticky top is minus the strip height (--preen-hdr-strip), so at the top of
 * the page the strip is shown with the header below it, the strip scrolls away with the page and the header then
 * sticks at the top of the screen. Every distance and threshold below is measured from the header's own top (the
 * strip's bottom); pages without the strip measure a strip of 0 and sticky top 0, as before. On phones the strip
 * also trims the white space above the logo (CSS variable --preen-hdr-top-trim on the logo column): the compact
 * header is computed as if that space were still there and lifted that much less, so it looks the same on every page.
 * Every state change is a transform and nothing else (no size, margin or position
 * change), so the page content never moves and the browser animates it on the compositor:
 *   hidden  = the wrapper translate3d(0, -(the header's height below the strip + overhang + 1px), 0), whole pixels
 *   compact = the wrapper translate3d(0, -dy, 0) (phones: dy = half the space above the beige box) and the logo
 *             translate3d(0, logo-y, 0) scale(.5); dy and logo-y are measured here, never per scroll.
 * Timing in the CSS: 280 ms decelerating (show, compact, back to full), 250 ms accelerating (hide); none under
 * prefers-reduced-motion. Hysteresis: the header slides away only after 24 px of travel down from the highest point
 * since it was shown, comes back only after 32 px of travel up from the lowest point since it hid, and a slide
 * that has started is never reversed within 300 ms. Compact (phones) switches off at scrollY <= strip + dy + band
 * (the strip, the lift, and the see-through band under the beige box, the wrapper's margin) and on at scrollY
 * >= that + 24: below it the full header is shown, so neither a gap nor the page's own top margin can show under
 * the lifted beige box. Tablets (no lift): off at <= strip + 2, on at >= strip + 14.
 * The logo gets its own compositor layer (will-change: transform) while active and scrolled away from the very top
 * (class preen-hdr--scrolled); at scrollY 0 it is not promoted, because a promoted image is resampled differently in
 * Chromium and the top of the page must stay pixel-identical to the header as before.
 * Per scroll: the passive scroll listener only requests one animation frame; that frame reads the scroll position
 * (nothing else) and toggles a class only when the state changes.
 * Sizes are measured on activation, load, resize and the header's ResizeObserver only, and the CSS variables and
 * scroll-padding-top are written only when a measured value changes.
 * Anchor jumps (#hash on arrival, hashchange) keep the header out of the way until the jump has settled, so a page
 * script that scrolls a target near the top (the FAQ auto-open) never leaves it under the header.
 */
(function () {
  'use strict';
  if (window.__preenHeaderAutohide) return;
  window.__preenHeaderAutohide = true;

  var QUERY = '(max-width: 1024.4999px)';
  var MARK = 'preen-hdr';            // the header wrapper once found (inert by itself)
  var ON = 'preen-hdr--on';          // on <html> and the wrapper while active (phone/tablet)
  var HIDDEN = 'preen-hdr--hidden';  // on the wrapper while slid out of view
  var COMPACT = 'preen-hdr--compact';  // on the wrapper while active and away from the top of the page (also while hidden)
  var SCROLLED = 'preen-hdr--scrolled';  // on the wrapper while active and scrollY > 0 (promotes the logo layer)
  var LOGO = '.pf-a0d085e054e2-35_ .pf-a0d085e054e2-37_';  // the phone/tablet logo (page-builder phone/tablet track)
  var BOX = '.pf-a0d085e054e2-35_ .pf-a0d085e054e2-38_';   // the beige box under it (phones) or right of it (tablets)
  var STRIP = '.preen-info-strip';   // the static info strip above the header, in the same wrapper (home, collection, product)
  var SCALE = 0.5;                   // compact logo size; the CSS scale(.5) is the same number
  var HIDE_AFTER = 24;               // px of travel down, from the highest point since shown, before it slides away
  var SHOW_AFTER = 32;               // px of travel up, from the lowest point since hidden, before it slides back
  var BAND = 24;                     // phones: compact off at scrollY <= dy (the lift), on at scrollY >= dy + BAND
  var TAB_OFF = 2, TAB_ON = 14;      // tablets (no lift): compact off at <= 2, on at >= 14
  var LOCK = 300;                    // ms a started slide runs before the opposite slide may start
  var SETTLE = 600;                  // ms without scrolling that ends an anchor jump

  var doc = document.documentElement;
  var mq = window.matchMedia ? window.matchMedia(QUERY) : null;
  var el = null, ro = null;
  var active = false, hidden = false, compact = false, scrolled = false, queued = false, sized = false;
  var holding = false, holdTimer = 0, loaded = document.readyState === 'complete';
  var lockUntil = 0, lockTimer = 0;
  var refY = 0, showLimit = 0, fullAt = 2, compactAt = 14;
  var docH = 0, viewH = 0, viewW = 0, maxY = Infinity;
  var written = {};

  function now() { return window.performance && performance.now ? performance.now() : Date.now(); }

  // the header-group section that holds the Preen header markup (never the generated section id)
  function findHeader() {
    var list = document.querySelectorAll('.shopify-section-group-header-group');
    for (var i = 0; i < list.length; i++) {
      var g = list[i];
      if (g.parentNode === document.body && g.querySelector('.__pf_B1bG0qIZ, [data-section-id="pf-a219"]')) return g;
    }
    return null;
  }

  // scroll position clamped to the real range, so iOS rubber-band overscroll never toggles the header. Reads only
  // the scroll offset; the range comes from the document and viewport heights cached on activation, load and resize.
  // Only when the offset passes the cached end of the page (rubber-band at the bottom, or the page has grown since) is
  // the page height read again, once per such frame.
  function pageY() {
    var y = window.pageYOffset || 0;
    if (y < 0) return 0;
    if (y > maxY) {
      docH = doc.scrollHeight;
      setRange();
      if (y > maxY) y = maxY;
    }
    return y;
  }

  function setRange() { maxY = docH > 0 ? Math.max(0, docH - viewH) : Infinity; }

  // a CSS variable on the wrapper (or html's scroll-padding-top), written only when its value changes
  function write(name, value) {
    if (written[name] === value) return;
    written[name] = value;
    if (name === 'scroll-padding-top') doc.style.scrollPaddingTop = value;
    else el.style.setProperty(name, value);
  }

  // never hide while the visitor is typing in the header (search box, keyboard open) or its results/menus are open
  function holdOpen() {
    var a = document.activeElement;
    if (a && a !== document.body && el.contains(a)) return true;
    return !!el.querySelector('[aria-expanded="true"], details[open], predictive-search[open]');
  }

  // after a slide starts, the opposite slide waits LOCK ms (then the state is checked again)
  function locked() { return now() < lockUntil; }
  function startLock() {
    lockUntil = now() + LOCK;
    window.clearTimeout(lockTimer);
    lockTimer = window.setTimeout(queue, LOCK + 20);
  }

  function setHidden(h, instant) {
    if (h === hidden) return;
    hidden = h;
    if (h) el.classList.add(HIDDEN); else el.classList.remove(HIDDEN);
    if (!instant) startLock();
  }

  function setCompact(c) {
    if (c === compact) return;
    compact = c;
    if (c) el.classList.add(COMPACT); else el.classList.remove(COMPACT);
  }

  function setScrolled(s) {
    if (s === scrolled) return;
    scrolled = s;
    if (s) el.classList.add(SCROLLED); else el.classList.remove(SCROLLED);
  }

  // compact by scroll position only (also while slid away), with hysteresis between the two thresholds; scrolled
  // while away from the very top
  function syncCompact(y) {
    setCompact(compact ? y > fullAt : y >= compactAt);
    setScrolled(y > 0);
  }

  // a state change without animation (activation part-way down a page); --preen-hdr-t switches the CSS transitions off
  function instantly(fn) {
    el.style.setProperty('--preen-hdr-t', 'none');
    fn();
    getComputedStyle(el).transform;
    var logo = el.querySelector(LOGO);
    if (logo) getComputedStyle(logo).transform;
    el.style.removeProperty('--preen-hdr-t');
  }

  // where the compact logo and header go (phones), from the layout with the logo's own transform taken out:
  // dy = how far the header moves up = half the space above the beige box; ly = the logo's extra move, so the
  // space above the logo and between the logo and the beige box are both halved. Tablets (logo left of the beige
  // box): the beige box sets the header height, so the header stays put and the logo shrinks about its centre.
  // Measured from top0, the header's own top (below the strip); band (phones) = the see-through space between the
  // beige box and the page below the header (bottom0 = the wrapper bottom plus its margin).
  function geometry(top0, bottom0) {
    var g = { dy: 0, ly: 0, band: 0 };
    var logo = el.querySelector(LOGO), box = el.querySelector(BOX);
    if (!logo || !box) return g;
    var lr = logo.getBoundingClientRect(), br = box.getBoundingClientRect();
    if (!(lr.width > 0 && lr.height > 0 && br.height > 0)) return g;
    var cs = getComputedStyle(logo), h = parseFloat(cs.height) || lr.height;
    var m = /^matrix\(([^)]+)\)$/.exec(cs.transform || ''), d = 1, f = 0;
    if (m) { var v = m[1].split(','); d = parseFloat(v[3]) || 1; f = parseFloat(v[5]) || 0; }
    var top = lr.top - top0 - (h / 2) * (1 - d) - f;    // transform-origin is the logo's centre
    var boxTop = br.top - top0;
    if (top + h <= boxTop + 1) {
      // a page may trim the space above the logo (--preen-hdr-top-trim, set with the info strip on phones): the compact
      // geometry is taken from the untrimmed header and the lift reduced by the trim, so the compact header is identical
      var trim = Math.round(parseFloat(cs.getPropertyValue('--preen-hdr-top-trim')) || 0);
      top += trim;
      boxTop += trim;
      // whole pixels: a will-change layer keeps the sub-pixel phase it was first rasterized at
      g.dy = Math.round((1 - SCALE) * boxTop);
      var room = boxTop - g.dy - SCALE * h;                  // space above the beige box for the two gaps
      var above = boxTop - h > 0 ? room * top / (boxTop - h) : 0;   // split as before: above : between
      g.ly = Math.round((above - (top - g.dy) - (h / 2) * (1 - SCALE)) * 100) / 100;
      g.dy -= trim;                                          // the trimmed space is not there to lift away
      g.band = Math.max(0, Math.ceil(bottom0 - br.bottom - 0.01));
    }
    return g;
  }

  // the header box plus what its content hangs below it: the slide distance, the always-shown zone at the top of
  // the page, and html's scroll-padding-top (focus and anchor scrolling never land under the header); plus the
  // compact geometry. The info strip at the top of the wrapper (sh px, 0 when absent) only sets the sticky offset and
  // shifts every threshold; the header's own height h is measured below it. Runs on activation, load, resize and
  // ResizeObserver only. Every read before every write.
  function measure() {
    sized = false;
    if (!el || !active) return;
    var r = el.getBoundingClientRect(), bottom = r.bottom;
    var s = el.querySelector(STRIP), sr = s ? s.getBoundingClientRect() : null;
    var mb = parseFloat(getComputedStyle(el).marginBottom) || 0;   // the see-through band below the header (a margin)
    var sh = sr && sr.height > 0 ? Math.max(0, Math.min(r.height, Math.round((sr.bottom - r.top) * 100) / 100)) : 0;
    var kids = el.getElementsByTagName('*');
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i].getBoundingClientRect();
      if (k.width > 0 && k.height > 0 && k.bottom > bottom && getComputedStyle(kids[i]).visibility !== 'hidden') bottom = k.bottom;
    }
    var extra = Math.max(0, Math.ceil(bottom - r.bottom));
    var h = Math.ceil(r.height - sh) + extra;               // the header below the strip
    var g = geometry(r.top + sh, r.bottom + mb);
    showLimit = sh + h + Math.round(mb);                     // its whole place: strip, header, see-through band
    fullAt = g.dy > 0 ? sh + g.dy + g.band : sh + TAB_OFF;
    compactAt = g.dy > 0 ? fullAt + BAND : sh + TAB_ON;
    write('--preen-hdr-strip', sh + 'px');                  // the sticky top is minus this
    write('--preen-hdr-extra', extra + 'px');
    write('--preen-hdr-hide', (h + 1) + 'px');              // the slide-away distance, in whole pixels
    write('--preen-hdr-dy', g.dy + 'px');
    write('--preen-hdr-logo-y', g.ly + 'px');
    write('scroll-padding-top', h + 'px');
  }

  // an element with the URL's #id (the target of an anchor jump)
  function anchorTarget() {
    var h = window.location.hash;
    if (!h || h.length < 2) return null;
    var id = h.slice(1);
    try { id = decodeURIComponent(id); } catch (e) { /* keep it raw */ }
    return document.getElementById(id);
  }

  // during an anchor jump the header stays out of the way; SETTLE ms without scrolling (or any input) ends it
  function hold(ms) {
    holding = true;
    window.clearTimeout(holdTimer);
    if (ms) holdTimer = window.setTimeout(release, ms);
  }
  function release() {
    window.clearTimeout(holdTimer);
    holding = false;
    refY = pageY();
  }
  function onAnchor() {
    if (!active || !anchorTarget()) return;
    hold(SETTLE);
    var y = pageY();
    if (y > showLimit) setHidden(true);
    syncCompact(y);
  }

  // one rAF per burst of scroll events; reads the scroll position only
  function update() {
    queued = false;
    if (!active) return;
    if (sized) measure();
    var y = pageY();
    if (y <= showLimit) {                       // at the top the header is always shown (it covers its own place)
      setHidden(false);
      refY = y;
    } else if (holding) {
      setHidden(true);
      if (loaded) hold(SETTLE);
      refY = y;
    } else if (!hidden) {
      if (y < refY) refY = y;                   // the highest point since the header was shown
      if (y - refY > HIDE_AFTER && !locked() && !holdOpen()) { setHidden(true); refY = y; }
    } else {
      if (y > refY) refY = y;                   // the lowest point since the header hid
      if (refY - y > SHOW_AFTER && !locked()) { setHidden(false); refY = y; }
    }
    syncCompact(y);
  }

  function queue() {
    if (!active || queued) return;
    queued = true;
    window.requestAnimationFrame(update);
  }

  // a new width can change the header; a new height (the iOS toolbar) only changes the scroll range
  function onResize() {
    if (!active) return;
    viewH = window.innerHeight;
    setRange();
    if (window.innerWidth !== viewW) {
      viewW = window.innerWidth;
      sized = true;
      queue();
    }
  }

  // ResizeObserver on the header: it changed size (fonts, images); runs after layout
  function onObserved() {
    if (active) measure();
  }

  function onFocusIn() {
    if (!active) return;
    if (holding) release();
    setHidden(false);
    syncCompact(pageY());
  }

  function onInput() {
    if (holding) release();
  }

  function activate() {
    if (active || !el) return;
    active = true;
    hidden = false;
    compact = false;
    scrolled = false;
    written = {};
    el.classList.remove(HIDDEN);
    el.classList.remove(COMPACT);
    el.classList.remove(SCROLLED);
    doc.classList.add(ON);
    el.classList.add(ON);
    viewH = window.innerHeight;
    viewW = window.innerWidth;
    docH = doc.scrollHeight;
    setRange();
    measure();
    var y = pageY();
    refY = y;
    // opened part-way down (an anchor link, the back button): start slid away (or compact), without animating
    instantly(function () {
      if (y > showLimit) setHidden(true, true);
      syncCompact(y);
    });
    if (anchorTarget()) hold(loaded ? SETTLE : 0);   // until the page's own anchor scrolling is done (load + SETTLE)
    if (window.ResizeObserver) {
      ro = ro || new ResizeObserver(onObserved);
      ro.observe(el);
    }
  }

  function deactivate() {
    if (!active) return;
    active = false;
    hidden = false;
    compact = false;
    scrolled = false;
    holding = false;
    window.clearTimeout(holdTimer);
    window.clearTimeout(lockTimer);
    lockUntil = 0;
    if (ro) ro.disconnect();
    doc.classList.remove(ON);
    doc.style.scrollPaddingTop = '';
    if (el) {
      el.classList.remove(ON);
      el.classList.remove(HIDDEN);
      el.classList.remove(COMPACT);
      el.classList.remove(SCROLLED);
      el.style.removeProperty('--preen-hdr-extra');
      el.style.removeProperty('--preen-hdr-hide');
      el.style.removeProperty('--preen-hdr-dy');
      el.style.removeProperty('--preen-hdr-logo-y');
      el.style.removeProperty('--preen-hdr-strip');
      el.style.removeProperty('--preen-hdr-t');
    }
    written = {};
  }

  // phone/tablet on, laptop/desktop off; re-evaluated on every change (iPad rotation 1024 <-> 1180/1194)
  function apply() {
    if (mq && mq.matches) activate(); else deactivate();
  }

  function init() {
    var found = findHeader();
    if (found !== el) {
      deactivate();
      el = found;
      if (!el) return;
      el.classList.add(MARK);
      el.addEventListener('focusin', onFocusIn);
    }
    apply();
  }

  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('hashchange', onAnchor);
  window.addEventListener('load', function () {
    loaded = true;
    if (holding) hold(SETTLE);
    if (active) { docH = doc.scrollHeight; setRange(); sized = true; queue(); }
  });
  ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach(function (t) {
    window.addEventListener(t, onInput, { passive: true });
  });
  if (mq) {
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function () { window.setTimeout(init, 0); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
